import * as Sentry from '@sentry/browser'
import { Vue as SentryIntegrationsVue } from '@sentry/integrations'
import { Event, EventHint } from '@sentry/types'

import { browserIs, browserCan } from 'bits/browser'

const API_KEY = 'https://7fbb2288ec794bef8ae6cad3689c63ba@sentry.io/22080'

interface InitOptions {
  vue?: any,
  environment?: string,
  release?: string,
  userId?: number,
  locale?: string,
}

interface CaptureOptions {
  level?: string,
  context?: any,
}

// Turn this on if sentry breadcrumbs are being unhelpful.
// function beforeBreadcrumb (breadcrumb: Breadcrumb): Breadcrumb|null {
//   return breadcrumb.category === 'sentry' ? null : breadcrumb
// }

function beforeSend (event: Event, hint: EventHint): Event|null {
  if (browserIs('ie')) return null
  if (
    event.exception &&
    event.exception.values &&
    event.exception.values[0] &&
    event.exception.values[0].type === 'UnhandledRejection'
  ) {
    return null
  }

  // Fix issue #1504 in github.com/padlet/mozart
  // Only allow this event through on the "staging" environment.
  if (event.environment !== 'staging' && hint && hint.originalException) {
    let message = ''
    if (typeof hint.originalException === 'string') {
      message = hint.originalException
    } else {
      message = (hint.originalException as Error).message
    }
    // mobile safari and iOS error
    // Look at https://sentry.io/organizations/padlet-y9/issues/1050542665/events/f4ca2db73b0b46d1af66b5c50af14a7c/?project=22080&query=is%3Aunresolved
    if (message.match(/Software caused connection abort/)) {
      return null
    }
  }
  return event
}

function init (options: InitOptions): void {
  const sentryOptions: Sentry.BrowserOptions = {
    dsn: API_KEY,
    environment: options.environment || 'development',
    release: options.release || 'unknown',
    defaultIntegrations: false,
    beforeSend,
    // beforeBreadcrumb,
  }

  // This is the default, but we are keeping the list here to be more aware of what's happening.
  const integrations: any[] = [
    new Sentry.Integrations.InboundFilters(),
    new Sentry.Integrations.FunctionToString(),
    new Sentry.Integrations.TryCatch(),
    new Sentry.Integrations.Breadcrumbs(),
    new Sentry.Integrations.GlobalHandlers({ onerror: true, onunhandledrejection: true }),
    new Sentry.Integrations.LinkedErrors(),
    new Sentry.Integrations.UserAgent(),
  ]

  // We don't want to initiate vue integration in development because
  // of a known error in vue where attaching an error handler stops
  // logging to the console.
  if (sentryOptions.environment !== 'development') {
    integrations.push(
      new SentryIntegrationsVue({
        Vue: options.vue,
        attachProps: true,
      })
    )
  }
  sentryOptions.integrations = integrations

  Sentry.init(sentryOptions)
  if (options.userId || options.locale) {
    Sentry.configureScope((scope): void => {
      scope.setTag('cookies', browserCan('cookies') ? 'yes' : 'no')
      options.userId && scope.setUser({ id: options.userId.toString() })
      options.locale && scope.setTag('locale', options.locale)
    })
  }
}

function captureException (e: any, options: CaptureOptions = {}): void {
  Sentry.withScope((scope): void => {
    if (options.context) {
      Object.keys(options.context).forEach((key): void => {
        scope.setExtra(key, options.context[key])
      })
    }
    Sentry.captureException(e)
  })
}

function captureMessage (message: string, options: CaptureOptions = {}): void {
  Sentry.withScope((scope): void => {
    if (options.context) {
      Object.keys(options.context).forEach((key): void => {
        scope.setExtra(key, options.context[key])
      })
    }
    Sentry.captureMessage(message)
  })
}

export { init, captureMessage, captureException }
